import { ModelAction, ModelName } from '@casops/store/api';
import { ModelWithId } from '@casops/store/apis/commonModels';
import { UseMutationHook } from '@casops/store/types';
import { SuccessNotificationContext } from '@dmc/ui';
import { RefObject, useCallback, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export interface UseModelActionDialogProps<
  TQueryArg extends ModelWithId = ModelWithId,
  TReturnType = void,
> {
  modelName: ModelName;
  modelAction: ModelAction;
  useModelMutation: UseMutationHook<TQueryArg, TReturnType>;
  queryArg: TQueryArg;
  navigateTo?: string;
}

export interface UseModelActionDialogReturn {
  dialogRef: RefObject<HTMLVdsDialogElement>;
  onAction: () => Promise<void>;
  openDialog: () => void;
  isError: boolean;
  isLoading: boolean;
}

export const useModelActionDialog = <
  TQueryArg extends ModelWithId = ModelWithId,
  TReturnType = void,
>({
  modelName,
  modelAction,
  useModelMutation,
  queryArg,
  navigateTo,
}: UseModelActionDialogProps<TQueryArg, TReturnType>) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccessMessage } = useContext(SuccessNotificationContext);

  const dialogRef = useRef<HTMLVdsDialogElement>(null);

  const [triggerMutation, { error, isLoading }] = useModelMutation();

  const mutationCallback = useCallback(async (): Promise<TReturnType> => {
    return await triggerMutation(queryArg).unwrap();
  }, [triggerMutation, queryArg]);

  const onAction = useCallback(async () => {
    try {
      await mutationCallback();
      dialogRef?.current?.close();
      showSuccessMessage(t(`Model.${modelAction}.Success`, { modelName }));
      if (navigateTo) {
        navigate(navigateTo);
      }
    } catch (err) {
      console.error(err);
    }
  }, [t, modelName, modelAction, mutationCallback, showSuccessMessage, navigateTo, navigate]);

  const openDialog = useCallback(() => {
    dialogRef?.current?.open();
  }, []);

  return {
    dialogRef,
    onAction,
    openDialog,
    isError: Boolean(error),
    isLoading,
  };
};
